import { List, Map } from "immutable";
import { isEmpty, isNil } from "lodash";

import { colorMap, colorMapHexLookup } from "../../constants/colorOptions";
import compactDisplays from "../../constants/compactDisplay";
import { DependencyTypes } from "../../constants/dependencyTypes";
import { CAN_IMPORT_FIELD, CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT } from "../../constants/importExportFile";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import { USER_INPUT_BOOLEAN_VALUES } from "../../constants/userInputValueTypes";
import batchOperations from "../../modules/batchOperations";
import { batchActions } from "../../modules/chainReducer";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import { dateWithString, endDateWithString } from "../../modules/definition/calendar/dateFunctions/dateWithString";
import {
  getReverseTimespan,
  timespanForPrecision,
  timespanForString
} from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { colorSelectors } from "../../modules/definition/colors";
import { typeSelectors } from "../../modules/definition/types";
import { dependencyOperations } from "../../modules/dependencies";
import { itemOperations } from "../../modules/items";
import { narrativeOperations } from "../../modules/narrative";
import { relationshipOperations, relationshipSelectors } from "../../modules/relationships";
import { tagOperations } from "../../modules/tags";
import urlUtils from "../../utils/urlUtils";

const getCsvImportDataOnly = (rawData, hasHeaderRow) => {
  const count = rawData.count();
  let data = List();
  if (count > 0) {
    data = hasHeaderRow ? rawData.shift() : rawData;
  }
  return data;
};

/**
 * Split the raw data into header and data
 * @param {List} rawData
 * @param {bool} hasHeaderRow
 */
const splitCsvImportData = (rawData, hasHeaderRow) => {
  const count = rawData.count();
  let header = List();
  let data = List();
  if (count > 0) {
    header = hasHeaderRow ? rawData.get(0) : List();
    data = hasHeaderRow ? rawData.shift() : rawData;
  }
  return { header, data };
};

const parseLabelValue = strValue => {
  const str = strValue.trim();
  const matched = str ? true : false;
  const misMatched = !matched;
  const parsedValue = matched ? str : null;
  return { parsedValue, matched, misMatched };
};

const parseTextValue = strValue => {
  const str = strValue.trim();
  const matched = Boolean(str);
  return { parsedValue: str ? str : null, matched, misMatched: 0 };
};

/**
 *
 * @param {string} strValue
 * @param {Map} typesById
 * @param {string} defaultItemType
 * @param {bool} allowNarrativeOnlyType If the narrative only type is not allowed, should return the parsed value but record misMatched
 */
const parseTypeValue = (strValue, typesById, defaultItemType, allowNarrativeOnlyType) => {
  const str = trimString(strValue).toLowerCase();
  let parsedValue = defaultItemType;
  let matched = false;
  let misMatched = false;
  if (str) {
    const type = typesById.find(t => {
      return t.label.toLowerCase() === str || t.id.toLowerCase() === str;
    });
    if (type) {
      parsedValue = type.id;
      // If the narrative only type is not allowed, should return the parsed value but record misMatched
      matched = allowNarrativeOnlyType || !typeSelectors.isNarrativeOnlyType(type);
    } else {
      misMatched = true;
    }
  }
  return { matched, misMatched, parsedValue };
};

const parseColorValue = (strValue, colorsById, nearestColorMap) => {
  let color = null;
  let matched = false;
  let misMatched = false;

  const str = trimString(strValue).toLowerCase();

  if (str) {
    // By label
    let foundColor = colorsById.find(c => c.label.toLowerCase() === str || c.id.toLowerCase() === str);
    if (foundColor) {
      // Found from the existed colors map
      color = foundColor.id;
      matched = true;
    } else if (colorMap[strValue]) {
      // Found color name
      color = strValue;
      matched = true;
    } else {
      // Try match the hex values
      foundColor = colorMapHexLookup[strValue] || colorMapHexLookup[strValue.toUpperCase()];
      if (foundColor) {
        color = foundColor;
        matched = true;
      } else {
        // Find the closest swatch color
        foundColor = colorSelectors.getNearestColorNameByHexCode(strValue, nearestColorMap);
        if (foundColor) {
          color = foundColor;
          matched = true;
        } else {
          matched = false;
          misMatched = true;
        }
      }
    }
  }

  return { matched, misMatched, parsedValue: color };
};

const parseDateValue = (strValue, calendar, isEndDate) => {
  const str = trimString(strValue);
  let parsedValue = null;
  let matched = false;
  let misMatched = false;
  if (str) {
    parsedValue = isEndDate
      ? endDateWithString(calendar, strValue, null, null, true)
      : dateWithString(calendar, strValue);
    matched = isDateValid(parsedValue);
    misMatched = !matched;
  }

  return { parsedValue, matched, misMatched };
};

const parseDurationValue = (strValue, defaultPrecision, calendar) => {
  const str = strValue.trim();
  let parsedValue = null;
  let matched = false;
  let misMatched = false;
  if (str) {
    if (!isNaN(str)) {
      matched = true;
      misMatched = !matched;
      parsedValue = timespanForPrecision(defaultPrecision, parseInt(str));
    } else {
      parsedValue = timespanForString(str, calendar);
      matched = !isNil(parsedValue);
      misMatched = !matched;
    }
  }

  return { parsedValue, matched, misMatched };
};

const parseMultipleValue = strValue => {
  let matched = 0;
  let misMatched = 0;
  const valueArr = strValue.split(",");
  const parsedValue = List(valueArr)
    .map(v => v.trim())
    .filter(v => v);
  matched = parsedValue.count();
  return { matched, misMatched, parsedValue: matched ? parsedValue : null };
};

const parseBooleanValue = strValue => {
  const str = trimString(strValue).toLowerCase();
  let matched = false;
  let misMatched = false;
  let parsedValue = null;
  if (str) {
    if (USER_INPUT_BOOLEAN_VALUES.true.includes(str)) {
      matched = true;
      parsedValue = true;
    } else if (USER_INPUT_BOOLEAN_VALUES.false.includes(str)) {
      matched = true;
      parsedValue = false;
    } else {
      matched = false;
      misMatched = true;
    }
  }
  return { matched, misMatched, parsedValue };
};

const parseIntegerValue = strValue => {
  const str = trimString(strValue);
  let matched = false;
  let misMatched = false;
  let parsedValue = null;
  if (str) {
    if (!isNaN(str)) {
      matched = true;
      misMatched = false;
      parsedValue = parseInt(str);
    } else {
      misMatched = true;
    }
  }

  return { matched, misMatched, parsedValue };
};

const parseFloatValue = (strValue, decimals) => {
  const str = trimString(strValue);
  let matched = false;
  let misMatched = false;
  let parsedValue = null;
  if (str) {
    if (!isNaN(str)) {
      matched = true;
      misMatched = false;
      parsedValue = isNil(decimals) ? parseFloat(str) : parseFloat(str).toFixed(decimals);
    } else {
      misMatched = true;
    }
  }

  return { matched, misMatched, parsedValue };
};

const parseTagsValue = (strValue, shouldCreateTag, tagsByTag) => {
  const { parsedValue: parsedList, matched: listMatched } = parseMultipleValue(strValue);
  let parsedValue = List();
  let matched = 0;
  let misMatched = 0;

  if (listMatched > 0) {
    parsedList.forEach(t => {
      if (tagsByTag.get(t)) {
        // The tag is existed
        parsedValue = parsedValue.push(t);
        matched += 1;
      } else if (shouldCreateTag) {
        // create item when not matching
        parsedValue = parsedValue.push(t);
        matched += 1;
      } else {
        misMatched += 1;
      }
    });
  }

  parsedValue = parsedValue.count() > 0 ? parsedValue : null;
  return { parsedValue, matched, misMatched };
};

const parseBlockingValue = (strValue, itemsByLabel, newItemsByLabel) => {
  const { parsedValue: parsedList, matched: listMatched } = parseMultipleValue(strValue);
  let parsedValue = List();
  let matched = 0;
  let misMatched = 0;
  let newItems = List();
  if (listMatched > 0) {
    parsedList.forEach(id => {
      const blockingItem = getItemByLabelId(id, itemsByLabel, newItemsByLabel);
      if (blockingItem && blockingItem.get(CAN_IMPORT_FIELD) !== false) {
        // The target item is existed
        parsedValue = parsedValue.push(id);
        matched += 1;
      } else {
        misMatched += 1;
      }
    });
  }
  parsedValue = parsedValue.count() > 0 ? parsedValue : null;
  return { parsedValue, matched, newItems, misMatched };
};

const parseSuperValue = (strValue, itemsByLabel, newItemsByLabel) => {
  const str = trimString(strValue);
  let parsedValue = null;
  let matched = false;
  let misMatched = false;
  if (str) {
    const superItem = getItemByLabelId(str, itemsByLabel, newItemsByLabel);
    if (superItem && superItem.get(CAN_IMPORT_FIELD) !== false) {
      // The target item is existed
      parsedValue = str;
      matched = true;
    } else {
      misMatched = true;
    }
  }
  return { parsedValue, matched, misMatched };
};

const isValidLink = link => urlUtils.isUrl(link);

const parseLinksValue = strValue => {
  const { parsedValue: parsedList, matched: listMatched } = parseMultipleValue(strValue);
  let parsedValue = List();
  let matched = 0;
  let misMatched = 0;

  if (listMatched > 0) {
    parsedList.forEach(t => {
      if (isValidLink(t)) {
        // The tag is existed
        parsedValue = parsedValue.push(t);
        matched += 1;
      } else {
        misMatched += 1;
      }
    });
  }

  parsedValue = parsedValue.count() > 0 ? parsedValue : null;
  return { parsedValue, matched, misMatched };
};

const parseListValue = (strValue, property) => {
  const matchAllowed = property.allowed.find(a => a.label === strValue);
  const matched = matchAllowed ? 1 : 0;
  const misMatched = matchAllowed ? 0 : 1;
  const parsedValue = matchAllowed ? matchAllowed.id : null;
  return { matched, misMatched, parsedValue };
};

const parsePropertyValue = (strValue, property) => {
  switch (property.format) {
    case PropertyFormatIdentifier.boolean:
      return parseBooleanValue(strValue);
    case PropertyFormatIdentifier.integer:
    case PropertyFormatIdentifier.percentage:
      return parseIntegerValue(strValue);
    case PropertyFormatIdentifier.double:
    case PropertyFormatIdentifier.currency:
      return parseFloatValue(strValue);
    case PropertyFormatIdentifier.list:
      return parseListValue(strValue, property);
    case PropertyFormatIdentifier.text:
    case PropertyFormatIdentifier.multitext:
    default:
      return parseTextValue(strValue);
  }
};

const hasReferenceBetweenItemTypes = (
  typesById,
  referencesById,
  referenceAllIds,
  defaultItemType,
  subjectTypeId,
  objectTypeId
) => {
  const sType = subjectTypeId || defaultItemType;
  const oType = objectTypeId || defaultItemType;
  return !isNil(
    relationshipSelectors.getAllowedReferenceBetweenTypes(typesById, referencesById, referenceAllIds, sType, oType)
  );
};

const parseItemTypeRelValue = (
  strValue,
  subItem,
  columnType,
  itemsByLabel,
  newItemsByLabel,
  shouldCreateItem,
  defaultItemType,
  typesById,
  referencesById,
  referenceAllIds
) => {
  const { parsedValue: parsedList, matched: listMatched } = parseMultipleValue(strValue);
  let parsedValue = List();
  let matched = 0;
  let misMatched = 0;
  let newItems = List();
  const subType = subItem.get("type");

  // No reference between the subject item type and the column type (ie. the object item type)
  if (!hasReferenceBetweenItemTypes(typesById, referencesById, referenceAllIds, defaultItemType, subType, columnType)) {
    misMatched = parsedList ? parsedList.size : 0;
  } else if (listMatched > 0) {
    parsedList.forEach(id => {
      const objItem = getItemByLabelId(id, itemsByLabel, newItemsByLabel);
      const objType = objItem ? objItem.get("type") : null;
      // The target item is existed and the item type is matching the column type
      if (objItem && objItem.get(CAN_IMPORT_FIELD) !== false && objType === columnType) {
        parsedValue = parsedValue.push(id);
        matched += 1;
      } else if (shouldCreateItem) {
        // create item when not matching
        parsedValue = parsedValue.push(id);
        matched += 1;
        newItems = newItems.push(Map({ label: id, type: columnType, [CAN_IMPORT_FIELD]: true }));
      } else {
        misMatched += 1;
      }
    });
  }
  parsedValue = parsedValue.count() > 0 ? parsedValue : null;
  return { parsedValue, matched, newItems, misMatched };
};

const parseRefTypeRelValue = (
  strValue,
  subItem,
  columnType,
  itemsByLabel,
  newItemsByLabel,
  shouldCreateItem,
  referencesById,
  typesById,
  defaultItemType
) => {
  const { parsedValue: parsedList, matched: listMatched } = parseMultipleValue(strValue);
  let parsedValue = List();
  let matched = 0;
  let misMatched = 0;
  let newItems = List();
  // If has object items and the subject item type has this reference type
  const subjectType = subItem.get("type") || defaultItemType;

  if (listMatched > 0) {
    // subject item cannot have that reference type
    if (!subjectType || !typesById.get(subjectType).references.includes(columnType)) {
      misMatched = listMatched;
    } else {
      parsedList.forEach(id => {
        const objItem = getItemByLabelId(id, itemsByLabel, newItemsByLabel);
        const reference = referencesById.get(columnType);
        // The target item is existed AND can import AND reference types has its type
        if (
          objItem &&
          objItem.get(CAN_IMPORT_FIELD) !== false &&
          reference.types.includes(objItem.get("type") || defaultItemType)
        ) {
          parsedValue = parsedValue.push(id);
          matched += 1;
        } else if (shouldCreateItem) {
          // create item when not matching
          parsedValue = parsedValue.push(id);
          matched += 1;
          newItems = newItems.push(Map({ label: id, type: reference.types.get(0), [CAN_IMPORT_FIELD]: true }));
        } else {
          misMatched += 1;
        }
      });
    }
  }
  parsedValue = parsedValue.count() > 0 ? parsedValue : null;
  return { parsedValue, matched, newItems, misMatched };
};

const parseOtherColumnTypes = (
  columnType,
  strValue,
  item,
  typesById,
  referencesById,
  referenceAllIds,
  propertiesById,
  itemsByLabel,
  newItemsByLabel,
  columnOption,
  defaultItemType
) => {
  if (item) {
    if (typesById.get(columnType)) {
      // Relationship with an item of the item type
      return parseItemTypeRelValue(
        strValue,
        item,
        columnType,
        itemsByLabel,
        newItemsByLabel,
        columnOption,
        defaultItemType,
        typesById,
        referencesById,
        referenceAllIds
      );
    } else if (referencesById.get(columnType)) {
      // Relationship with an item of the reference type
      return parseRefTypeRelValue(
        strValue,
        item,
        columnType,
        itemsByLabel,
        newItemsByLabel,
        columnOption,
        referencesById,
        typesById,
        defaultItemType
      );
    }
  }
  if (propertiesById.get(columnType)) {
    return parsePropertyValue(strValue, propertiesById.get(columnType));
  }
  return { matched: false, misMatched: false, parsedValue: null };
};

const parseStringValueByColumnType = (
  strValue,
  item,
  columnType,
  defaultItemType,
  typesById,
  referencesById,
  referenceAllIds,
  colorsById,
  calendar,
  columnInfoOptions,
  propertiesById,
  itemsByLabel,
  newItemsByLabel,
  tagsByTag,
  allowNarrativeOnlyType,
  nearestColorMap
) => {
  const columnOption = columnInfoOptions.get(columnType);
  switch (columnType) {
    case "label":
      return parseLabelValue(strValue);
    case "internalId":
    case "summary":
      return parseTextValue(strValue);
    case "super":
      return parseSuperValue(strValue, itemsByLabel, newItemsByLabel);
    case "type":
      return parseTypeValue(strValue, typesById, defaultItemType, allowNarrativeOnlyType);
    case "color":
      return parseColorValue(strValue, colorsById, nearestColorMap);
    case "startDate":
    case "latestStartDate":
      return parseDateValue(strValue, calendar, false);
    case "endDate":
    case "earliestEndDate":
      return parseDateValue(strValue, calendar, true);
    case "duration":
      return parseDurationValue(strValue, columnOption, calendar);
    case "tags":
      return parseTagsValue(strValue, columnOption, tagsByTag);
    case "blocking":
      return parseBlockingValue(strValue, itemsByLabel, newItemsByLabel);
    case "links":
      return parseLinksValue(strValue);
    default:
      return parseOtherColumnTypes(
        columnType,
        strValue,
        item,
        typesById,
        referencesById,
        referenceAllIds,
        propertiesById,
        itemsByLabel,
        newItemsByLabel,
        columnOption,
        defaultItemType
      );
  }
};

const trimString = str => (str && str.trim() ? str.trim() : "");

const getItemLabelId = item => {
  return trimString(item.get("internalId")) || trimString(item.get("label"));
};

const convertItemListToLabelMap = itemList =>
  itemList.reduce((newMap, i) => {
    // Log both the label and the internalId as the label
    const label = i.get("label");
    const internalId = i.get("internalId");
    return newMap.withMutations(rm => {
      if (label) {
        rm.set(label, i);
      }
      if (internalId) {
        rm.set(internalId, i);
      }
    });
  }, Map());

const updateItemsValues = (
  itemsValues,
  relItemsValues,
  columnType,
  columnTypes,
  data,
  defaultItemType,
  typesById,
  referencesById,
  referenceAllIds,
  colorsById,
  dateOrder,
  twoDigitsToTwoThousandYear,
  inCalendar,
  columnInfoOptions,
  propertiesById,
  itemsByLabel,
  tagsByTag,
  allowNarrativeOnlyType,
  nearestColorMap
) => {
  let totalMatched = 0;
  let totalMisMatched = 0;
  let newValues = itemsValues;
  let newRelValues = relItemsValues;
  const calendar = inCalendar
    ? inCalendar.merge({ dateOrder: dateOrder, defaultToModernCentury: twoDigitsToTwoThousandYear })
    : null;

  if (columnType !== CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT) {
    let newItemsByLabel = convertItemListToLabelMap(itemsValues.concat(relItemsValues));
    const columnIndex = columnTypes.findIndex(ct => ct === columnType);
    if (columnIndex >= 0) {
      newValues = newValues.withMutations(rm => {
        data.forEach((row, rowIndex) => {
          const strValue = row.get(columnIndex) ? row.get(columnIndex) : "";
          const { parsedValue, matched, misMatched, newItems } = parseStringValueByColumnType(
            strValue,
            itemsValues.get(rowIndex),
            columnType,
            defaultItemType,
            typesById,
            referencesById,
            referenceAllIds,
            colorsById,
            calendar,
            columnInfoOptions,
            propertiesById,
            itemsByLabel,
            newItemsByLabel,
            tagsByTag,
            allowNarrativeOnlyType,
            nearestColorMap
          );
          if (!isNil(parsedValue)) {
            rm.setIn([rowIndex, columnType], parsedValue);
          }
          // Update the can import value
          rm.setIn([rowIndex, CAN_IMPORT_FIELD], canItemImport(rm.get(rowIndex), allowNarrativeOnlyType, typesById));
          // The newItems is from the relationship (by type / reference) columns
          if (newItems && newItems.count() > 0) {
            newItemsByLabel = newItemsByLabel.merge(convertItemListToLabelMap(newItems));
            newRelValues = newRelValues.concat(newItems);
          }
          totalMatched += matched === true ? 1 : matched > 0 ? matched : 0;
          totalMisMatched += misMatched === true ? 1 : misMatched > 0 ? misMatched : 0;
        });
      });
    }
  }
  return { matched: totalMatched, misMatched: totalMisMatched, newValues, newRelValues };
};

const clearItemValues = (itemsValues, columnType) => {
  return columnType ? itemsValues.map(i => i.delete(columnType)) : itemsValues;
};

const createTagValueMap = tagsMap => {
  return Map(
    tagsMap.reduce((values, tag) => {
      if (!values[tag]) {
        values[tag] = tag;
      }
      return values;
    }, {})
  );
};

const createItemLabelMap = (itemsById, typesById) => {
  return Map(
    itemsById.reduce((labels, i) => {
      const type = typesById.get(i.type);

      if (i.label && !labels[i.label]) {
        labels[i.label] = i;
      }
      if (type.compactDisplay === compactDisplays.IDENTIFIER && i.displayId && !labels[i.displayId]) {
        labels[i.displayId] = i;
      }
      if (type.compactDisplay === compactDisplays.SHORT_LABEL && i.shortLabel && !labels[i.shortLabel]) {
        labels[i.shortLabel] = i;
      }
      if (type.compactDisplay === compactDisplays.INITIALS && i.initials && !labels[i.initials]) {
        labels[i.initials] = i;
      }
      if (i.internalId && !labels[i.internalId]) {
        labels[i.internalId] = i;
      }
      return labels;
    }, {})
  );
};

const getDateAction = (id, item, field, createActionFunc) => {
  let action = [];
  const date = item.get(field);
  if (date) {
    action.push(createActionFunc([id], date.timestamp, date.precision));
  }
  return action;
};

/**
 * If start date is not available,
 * will try calculate that by end date and duration if they are both available
 * @param {string} id
 * @param {Map} item
 */
const getStartDateAction = (id, item, calendar) => {
  let action = [];
  let date = item.get("startDate");
  if (!isDateValid(date)) {
    const endDate = item.get("endDate");
    const duration = item.get("duration");
    if (isDateValid(endDate) && duration) {
      date = dateByAddingSpan(calendar, endDate, getReverseTimespan(duration));
    }
  }
  if (isDateValid(date)) {
    action.push(itemOperations.setPreferredStartDate([id], date.timestamp, date.precision));
  }
  return action;
};

const getEndDateAction = (id, item, isPriorOverDuration) => {
  let action = [];
  const date = item.get("endDate");
  // If end date is available,
  // has start date, need to choose end date or duration by isPriorOverDuration
  // no start date, can keep both
  if (date && (!isDateValid(item.get("startDate")) || isPriorOverDuration || !item.get("duration"))) {
    action.push(itemOperations.setPreferredEndDate([id], date.timestamp, date.precision));
  }
  return action;
};

const getDurationAction = (id, item, isEndDatePriorOverDuration) => {
  let action = [];
  const duration = item.get("duration");
  // If duration is available,
  // has start date, need to choose end date or duration by isEndDatePriorOverDuration
  // no start date, can keep both
  if (duration && (!isDateValid(item.get("startDate")) || !isEndDatePriorOverDuration || !item.get("endDate"))) {
    action.push(itemOperations.setPreferredDuration([id], duration));
  }
  return action;
};

const getTagsAction = (id, item) => {
  let action = [];
  const tags = item.get("tags");
  if (tags) {
    action.push(tagOperations.addTags([id], tags.toJS()));
  }
  return action;
};

const getLinksActions = (id, item) => {
  let actions = [];
  const links = item.get("links");
  if (links) {
    actions = links.map(l => batchOperations.createAttachmentAndAssignToItemsLinks([id], "", l)).toJS();
  }
  return actions;
};

const getPropertyValuesActions = (id, item, propertiesById) => {
  let actions = [];
  propertiesById.forEach((prop, propId) => {
    const propValue = item.get(propId);
    if (!isNil(propValue)) {
      actions.push(itemOperations.setPropertyValue([id], propId, propValue));
    }
  });
  return actions;
};

const getItemByLabelId = (labelId, itemsByLabel, newItemsByLabel) =>
  newItemsByLabel.get(labelId) || itemsByLabel.get(labelId);

const getRelationshipByItemTypeActions = (subItem, itemsByLabel, newItemsByLabel, newRelItemsByLabel, typesById) => {
  let actions = [];
  typesById.forEach((t, tId) => {
    const targetItems = subItem.get(tId);
    if (targetItems) {
      targetItems.forEach(objLabelId => {
        const subRealId = subItem.get("id");
        let objItem = getItemByLabelId(objLabelId, itemsByLabel, newItemsByLabel);
        // If the type of the object is not matching,
        // then it must be a created relationship item located in the newRelItemsByLabel
        objItem = !objItem || objItem.get("type") !== tId ? newRelItemsByLabel.get(objLabelId) : objItem;
        const objRealId = objItem.get("id");
        // Note: the reducer will give the default reference type
        actions.push(relationshipOperations.createRelationship([subRealId], null, [objRealId]));
      });
    }
  });
  return actions;
};

const getRelationshipByRefTypeActions = (
  subItem,
  itemsByLabel,
  newItemsByLabel,
  newRelItemsByLabel,
  referencesById
) => {
  let actions = [];
  referencesById.forEach((r, rId) => {
    const targetItems = subItem.get(rId);
    if (targetItems) {
      targetItems.forEach(objLabelId => {
        const subRealId = subItem.get("id");
        let objItem = getItemByLabelId(objLabelId, itemsByLabel, newItemsByLabel);
        // If the type the object is not matching the item types in the reference
        // then it must be a created relationship item located in the newRelItemsByLabel
        objItem = !objItem || !r.types.includes(objItem.get("type")) ? newRelItemsByLabel.get(objLabelId) : objItem;
        const objRealId = objItem.get("id");
        actions.push(relationshipOperations.createRelationship([subRealId], rId, [objRealId]));
      });
    }
  });
  return actions;
};

const getBlockingActions = (subItem, itemsByLabel, newItemsByLabel) => {
  let actions = [];
  const targetItems = subItem.get("blocking");
  if (targetItems) {
    targetItems.forEach(objLabelId => {
      const subRealId = subItem.get("id");
      const objItem = getItemByLabelId(objLabelId, itemsByLabel, newItemsByLabel);
      const objRealId = objItem.get("id");
      actions.push(dependencyOperations.createDependencies([subRealId], [objRealId], DependencyTypes.FS));
    });
  }

  return actions;
};

const getSuperActions = (childItem, itemsByLabel, newItemsByLabel) => {
  let actions = [];
  const superLabelId = childItem.get("super");
  if (superLabelId) {
    const childRealId = childItem.get("id");
    const superItem = getItemByLabelId(superLabelId, itemsByLabel, newItemsByLabel);

    if (superItem) {
      const superRealId = superItem.get("id");
      actions.push(itemOperations.setSuper([childRealId], superRealId));
    }
  }

  return actions;
};

const getAddToNarrativeAction = (id, itemTypeId, addToNarrative, typesById, lastNarrativeIndex) =>
  addToNarrative && typeSelectors.isNarrativeType(typesById.get(itemTypeId))
    ? [narrativeOperations.moveInNarrative([id], null, lastNarrativeIndex)]
    : [];

const getUpdateItemAction = (id, item) => [
  itemOperations.updateItem([id], { label: item.get("label"), summary: item.get("summary") })
];

// Create item or match the internal id
const getCreateAction = item => {
  let id = item.get("id");
  let action = null;
  if (!id) {
    action = itemOperations.createItem(item.get("type"));
    id = action.id;

    const internalId = item.get("internalId");
    if (internalId) {
      action = batchActions([action, itemOperations.setInternalId(id, internalId)]);
    }
  }
  return { action, id };
};

const createItemNormalFieldsActions = (
  item,
  columnInfoOptions,
  addToNarrative,
  propertiesById,
  calendar,
  typesById,
  lastNarrativeIndex
) => {
  let actions = [];
  const itemTypeId = item.get("type");
  const { action: createAction, id } = getCreateAction(item);
  if (createAction) {
    actions.push(createAction);
  }

  // Add to narrative ?
  actions = actions.concat(getAddToNarrativeAction(id, itemTypeId, addToNarrative, typesById, lastNarrativeIndex));

  // Update simple properties
  actions = actions.concat(getUpdateItemAction(id, item));

  // Color
  if (item.get("color")) {
    actions.push(itemOperations.editColor([id], item.get("color")));
  }
  // Start Date
  actions = actions.concat(getStartDateAction(id, item, calendar));
  // End Date
  actions = actions.concat(getEndDateAction(id, item, columnInfoOptions.get("endDate")));
  // Duration
  actions = actions.concat(getDurationAction(id, item, columnInfoOptions.get("endDate")));
  // Latest start date
  actions = actions.concat(getDateAction(id, item, "latestStartDate", itemOperations.setLatestStartDate));
  // Earliest end date
  actions = actions.concat(getDateAction(id, item, "earliestEndDate", itemOperations.setEarliestEndDate));
  // Tags
  actions = actions.concat(getTagsAction(id, item));
  // links
  actions = actions.concat(getLinksActions(id, item));
  // properties
  actions = actions.concat(getPropertyValuesActions(id, item, propertiesById));

  return { actions, id };
};

const createItemRelationshipFieldsActions = (
  item,
  itemsByLabel,
  newItemsByLabel,
  newRelItemsByLabel,
  typesById,
  referencesById
) => {
  let actions = [];

  const subItem = getItemByLabelId(getItemLabelId(item), itemsByLabel, newItemsByLabel);
  // super
  actions = actions.concat(getSuperActions(subItem, itemsByLabel, newItemsByLabel));
  // blocking
  actions = actions.concat(getBlockingActions(subItem, itemsByLabel, newItemsByLabel));
  // item types
  actions = actions.concat(
    getRelationshipByItemTypeActions(subItem, itemsByLabel, newItemsByLabel, newRelItemsByLabel, typesById)
  );
  // reference types
  actions = actions.concat(
    getRelationshipByRefTypeActions(subItem, itemsByLabel, newItemsByLabel, newRelItemsByLabel, referencesById)
  );
  return actions;
};

/**
 * 1. Has label
 * 2. Allow narrative only type OR is not narrative only type
 * @param {Map} item
 * @param {bool} allowNarrativeOnlyType
 * @param {Map} typesById
 */
const canItemImport = (item, allowNarrativeOnlyType, typesById) => {
  return (
    item &&
    !isEmpty(item.get("label")) &&
    (allowNarrativeOnlyType || !item.get("type") || !typeSelectors.isNarrativeOnlyType(typesById.get(item.get("type"))))
  );
};

const createImportActions = (
  itemsValues,
  relItemsValues,
  itemsByLabel,
  columnInfoOptions,
  addToNarrative,
  typesById,
  referencesById,
  propertiesById,
  calendar,
  lastRootIndexForNarrative
) => {
  let actions = [];
  let newItemsByLabel = convertItemListToLabelMap(itemsValues);
  let newRelItemsByLabel = convertItemListToLabelMap(relItemsValues);
  let lastNarrativeIndex = lastRootIndexForNarrative + itemsValues.count() + relItemsValues.count();

  const processCreateAndNormalActions = (i, itemsMap) => {
    let newItemsMap = itemsMap;
    if (i.get(CAN_IMPORT_FIELD)) {
      const { actions: createActions, id: newId } = createItemNormalFieldsActions(
        i,
        columnInfoOptions,
        addToNarrative,
        propertiesById,
        calendar,
        typesById,
        lastNarrativeIndex
      );
      // Push the actions
      actions = actions.concat(createActions);
      // Find the real id and update the newItemsByLabel on both label and internalId
      const label = i.get("label");
      const internalId = i.get("internalId");
      const realId = newId;
      newItemsMap = newItemsMap.withMutations(rm => {
        if (label) {
          rm.setIn([label, "id"], realId);
        }
        if (internalId) {
          rm.setIn([internalId, "id"], realId);
        }
      });
    }
    return newItemsMap;
  };

  // Normal fields
  // Normal importing items
  itemsValues.forEach(i => {
    newItemsByLabel = processCreateAndNormalActions(i, newItemsByLabel);
  });
  // Relationship items
  relItemsValues.forEach(i => {
    newRelItemsByLabel = processCreateAndNormalActions(i, newRelItemsByLabel);
  });

  // Relative fields
  itemsValues.forEach(i => {
    if (i.get(CAN_IMPORT_FIELD)) {
      const relationshipActions = createItemRelationshipFieldsActions(
        i,
        itemsByLabel,
        newItemsByLabel,
        newRelItemsByLabel,
        typesById,
        referencesById
      );
      actions = actions.concat(relationshipActions);
    }
  });
  return actions;
};

// So far this exporting is for testing only
const getActionFunctions = {
  getAddToNarrativeAction,
  getUpdateItemAction,
  getDateAction,
  getStartDateAction,
  getEndDateAction,
  getDurationAction,
  getTagsAction,
  getLinksActions,
  getPropertyValuesActions,
  getRelationshipByItemTypeActions,
  getRelationshipByRefTypeActions,
  getBlockingActions,
  getSuperActions
};

export {
  canItemImport,
  clearItemValues,
  createImportActions,
  createItemLabelMap,
  createTagValueMap,
  getActionFunctions,
  getCsvImportDataOnly,
  getItemLabelId,
  splitCsvImportData,
  updateItemsValues
};
